import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense, lazy } from "react";
import "./App.css";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import UploadSection from "./components/UploadSection";
import StatsCards from "./components/StatsCards";
import OverallDashboard from "./components/OverallDashboard";
import AppLauncher from "./components/AppLauncher";
import { Button, EmptyState, ErrorState, LoadingBar, PageHeader } from "./components/common/ui";
import { getMyModules, MODULES, setAccountsUser, getAccountsUser } from "./services/modulesApi";
import AuthGate from "./components/auth/AuthGate";
import { logout as accountLogout } from "./services/accountApi";
import { getPoStatus, listUploads, getMapping, deleteSession, archiveSession, toggleConfirmation, getOverall, getConsolidated, getConsolidatedPage, getExpiredPos, getVendorScore, getVendorResponses, sendBulkVendorEmails, getServerIp, listVendors, sendBuyerReminder, getReminderCooldowns, getAppSettings, listPendingUploads, getStyleRates, setStyleRate, getPendingBarcodes, markVendorResponseItReady, getCpApprovals } from "./services/api";
import { BUYERS, BUYER_COLORS, DEPT_TO_BUYER, getBuyer, computeStats, READONLY_BUYERS, fmtDate } from "./utils/buyerMapping";
// 2026-10-07 accounts: setCurrentUser / loadDepartmentMap / canSeeDepartment are added to buyerMapping.js by another agent; read via namespace so a missing export degrades gracefully.
import * as BM from "./utils/buyerMapping";
import { toolList } from "./utils/access";
import { useStyleProfitPref, showStyleProfitFor } from "./utils/stylePrefs";

// 2026-10-08 speed pass: tool pages load on first use, not with the first screen. Shell pieces (Header, Sidebar,
// launcher, sign-in, Overall dashboard, upload card) stay eager. Keep these consts above every component that renders them.
const MappingTable = lazy(() => import("./components/MappingTable"));
const OtbNotRaisedBanner = lazy(() => import("./components/OtbNotRaisedBanner")); // 2026-10-24 Ecom OTB reorder styles not raised
const ConsolidatedView = lazy(() => import("./components/ConsolidatedView"));
const ExpiredPoPage = lazy(() => import("./components/ExpiredPoPage"));
const VendorScorePage = lazy(() => import("./components/VendorScorePage"));
const VendorPdfsPage = lazy(() => import("./components/VendorPdfsPage"));
const VendorMasterPage = lazy(() => import("./components/VendorMasterPage"));
const VendorResponsesPage = lazy(() => import("./components/VendorResponsesPage"));
const AdminPortal = lazy(() => import("./components/AdminPortal"));
const UsersAccessPage = lazy(() => import("./components/admin/UsersAccessPage")); // 2026-10-13 team lead "Team" page
const BlacklistPage = lazy(() => import("./components/BlacklistPage"));
const BlockbusterPage = lazy(() => import("./components/BlockbusterPage"));
const PendingApprovalsPage = lazy(() => import("./components/PendingApprovalsPage"));
const CpApprovalsPage = lazy(() => import("./components/CpApprovalsPage"));
const BarcodesPage = lazy(() => import("./components/BarcodesPage"));
const NoBarcodeQueuePage = lazy(() => import("./components/NoBarcodeQueuePage"));
const ArsManagementPage = lazy(() => import("./components/ArsManagementPage"));
const RegionalWhApp = lazy(() => import("./components/RegionalWhApp"));
const StockRecoApp = lazy(() => import("./components/stockReco/StockRecoApp"));
const ChannelProfitApp = lazy(() => import("./components/channelProfit/ChannelProfitApp")); // 2026-10-27 Channel Profitability (docs/specs/channel-profit.md)
const EcomOtbApp = lazy(() => import("./components/ecomOtb/EcomOtbApp")); // 2026-10-24 Ecom OTB (docs/specs/ecom-otb.md)
const DiscountVerifyApp = lazy(() => import("./components/discountVerify/DiscountVerifyApp"));

const pageFallback = <div style={{ padding: "4px 0 16px" }}><LoadingBar /></div>;

// One toast at a time; a new toast replaces the old one and restarts its 3.5 s timer (the old timer used to cut it short).
function useToast() {
  const [toast, setToast] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const showToast = useCallback((msg, type = "info") => {
    clearTimeout(timer.current);
    setToast({ msg, type });
    timer.current = setTimeout(() => setToast(null), 3500);
  }, []);
  return [toast, showToast];
}

// -- 2026-10-07 accounts: bridge the signed-in user onto the legacy localStorage keys the Reorder screens still read --
const LEGACY_KEYS = ["reorder_device_token", "reorder_buyer", "reorder_admin_token", "reorder_approver_token", "reorder_display_name", "reorder_member_email", "reorder_is_primary_ecom", "reorder_is_sub_member", "reorder_sub_departments", "reorder_sub_vendors", "reorder_can_manage_team"];
let _warnedCanSee = false;
const canSeeDept = (d) => {
  if (typeof BM.canSeeDepartment === "function") return BM.canSeeDepartment(d);
  if (!_warnedCanSee) { _warnedCanSee = true; console.warn("[accounts] buyerMapping.canSeeDepartment missing - department filter is not applied client-side"); }
  return true;
};
// Access v2: derive the legacy PIN-era login name from the new user shape (spec "Legacy bridge").
const LEGACY_BUYER_NAMES = ["Saleem", "Nanik", "Salman", "DVC", "Shailendra", "Khadeer"];
function legacyLoginFor(user) {
  const team = user.team;
  if (team === "admin" || user.is_admin) return "Admin";
  if (team === "ecom") return "Ecom";
  if (team === "warehouse") return "Warehouse";
  if (team === "crm") return "CRM";
  if (team !== "buyer") return user.legacy_login || "";
  const first = String(user.name || "").trim().split(/\s+/)[0].toLowerCase();
  const hit = LEGACY_BUYER_NAMES.find((n) => n.toLowerCase() === first);
  if (hit) return hit;
  const depts = (user.departments || []).map((d) => String((d && d.name) || "").trim());
  const rmg = user.all_departments || (user.vendors || []).length > 0 || depts.some((d) => d && !DEPT_TO_BUYER[d]);
  return rmg ? "Shailendra" : (user.name || "");
}
function writeLegacyKeys(user) {
  const login = legacyLoginFor(user);
  const subMember = login === "Shailendra" && !user.all_departments;
  const depts = (user.departments || []).map((d) => (d && d.name) || "").filter(Boolean);
  try {
    ["reorder_device_token", "reorder_admin_token", "reorder_approver_token"].forEach((k) => localStorage.removeItem(k)); // stale PIN-era tokens
    localStorage.setItem("reorder_buyer", login);
    localStorage.setItem("reorder_display_name", user.name || "");
    localStorage.setItem("reorder_member_email", user.email || "");
    localStorage.setItem("reorder_is_sub_member", String(subMember));
    localStorage.setItem("reorder_sub_departments", JSON.stringify(subMember ? depts : []));
    localStorage.setItem("reorder_sub_vendors", JSON.stringify(subMember ? (user.vendors || []) : []));
  } catch (e) { console.error("[accounts] could not write session keys", e); }
}
// Single logout path for both shells (server session + legacy keys, then reload).
function accountsLogout() {
  accountLogout().catch((e) => console.error("[accounts] logout failed", e)).then(() => {
    LEGACY_KEYS.forEach((k) => { try { localStorage.removeItem(k); } catch (e) { /* ignore */ } });
    window.location.reload();
  });
}

const gateSpinner = <div className="state-center" style={{ height: "100vh" }}><div className="spinner" /></div>;

function AccountsBridge({ user, children }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    setReady(false);
    writeLegacyKeys(user);
    setAccountsUser(user);
    (async () => {
      try {
        if (typeof BM.setCurrentUser === "function") BM.setCurrentUser(user); else console.warn("[accounts] buyerMapping.setCurrentUser missing");
        if (typeof BM.loadDepartmentMap === "function") await BM.loadDepartmentMap(); else console.warn("[accounts] buyerMapping.loadDepartmentMap missing");
      } catch (e) { console.warn("[accounts] could not load department map", e); }
      if (alive) setReady(true);
    })();
    return () => { alive = false; };
  }, [user]);
  if (!ready) return gateSpinner;
  return <React.Fragment key={user.id || user.email}>{children}</React.Fragment>;
}

// Entry gate used by index.js: email + password sign-in (2026-10-12 remove-pin: the PIN gate and the mode probe are gone).
export function AuthRoot({ children }) {
  return <AuthGate>{(user) => <AccountsBridge user={user}>{children}</AccountsBridge>}</AuthGate>;
}

// Delete an upload session: the server checks the admin session; the admin confirms by typing DELETE (GitHub-style red-zone confirm).
function DeleteSessionModal({ session, onConfirm, onCancel }) {
  const [typed, setTyped]   = useState("");
  const [error, setError]   = useState("");
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const handle = async () => {
    if (typed.trim() !== "DELETE") { setError("Type DELETE to confirm"); return; }
    setDeleting(true);
    setError("");
    try { await onConfirm(typed.trim()); }
    catch (err) { setError(err.response?.data?.detail || "Could not delete the session"); setDeleting(false); }
  };

  return (
    <div className="ui-modal-overlay" onClick={onCancel}>
      <div className="ui-modal" role="dialog" aria-modal="true" aria-label="Delete session" onClick={(e) => e.stopPropagation()} style={{ borderTop: "3px solid var(--danger)" }}>
        <div className="ui-modal-head">
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--danger)" }}>Delete session</div>
        </div>
        <div className="ui-modal-body">
          <div style={{ color: "var(--text-muted)", marginBottom: 14, overflowWrap: "anywhere" }}>
            Permanently delete <strong style={{ color: "var(--text)" }}>{fmtDate(session.request_date) || session._id}</strong>
            {session.filename ? <> &mdash; {session.filename}</> : ""}?
            This cannot be undone.
          </div>
          <input ref={inputRef} type="text" placeholder="Type DELETE to confirm" value={typed}
            onChange={(e) => { setTyped(e.target.value); setError(""); }}
            onKeyDown={(e) => e.key === "Enter" && handle()}
            style={modalInput} />
          {error && <div role="alert" style={{ color: "var(--danger)", fontSize: 12, marginTop: 6 }}>{error}</div>}
        </div>
        <div className="ui-modal-foot">
          <Button variant="secondary" onClick={onCancel} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={handle} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ToggleConfirmModal({ session, onConfirm, onCancel }) {
  const hiding = session.confirmation_enabled !== false;
  return (
    <div className="ui-modal-overlay" onClick={onCancel}>
      <div className="ui-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="ui-modal-head">
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text)" }}>
            {hiding ? "Hide from Vendor Portal?" : "Show in Vendor Portal?"}
          </div>
        </div>
        <div className="ui-modal-body" style={{ color: "var(--text-muted)" }}>
          {hiding
            ? <>Vendors will <strong>no longer see</strong> styles from <strong>{fmtDate(session.request_date)}</strong> when they open their confirmation link.</>
            : <>Vendors will <strong>be able to see and confirm</strong> styles from <strong>{fmtDate(session.request_date)}</strong> again.</>
          }
        </div>
        <div className="ui-modal-foot">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button variant={hiding ? "danger" : "primary"} onClick={onConfirm}>
            {hiding ? "Hide from vendors" : "Show to vendors"}
          </Button>
        </div>
      </div>
    </div>
  );
}

const modalInput = {
  width: "100%", padding: "8px 12px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border-strong)",
  background: "var(--bg-input)", color: "var(--text)", fontSize: 14,
  boxSizing: "border-box", letterSpacing: 1,
};

// 2026-10-09 app-level Settings (header user menu > Settings). Reorder views that used to open these sections inside
// the Reorder Admin hub; they now land in the app Settings view on the same section (AdminPortal HUB_ALIASES).
const APP_SETTINGS_VIEWS = ["wa-reports", "api-export-log"];
// Who may open app Settings: the admin team (is_admin).
function canOpenAppSettings() {
  const u = getAccountsUser();
  return !!u && (u.team === "admin" || !!u.is_admin);
}
// 2026-10-13 team leads (Buyer / Ecom person with `manages`, set by an admin): user menu > Team = Users & Access limited to their team.
function isTeamLead() {
  const u = getAccountsUser();
  return !!u && !(u.team === "admin" || u.is_admin) && !!u.manages;
}

// Reorder Tracker module (everything that used to be the whole app). Mounted only while the Reorder module is open,
// so Reorder data is never fetched for logins that cannot use it (Warehouse / CRM).
function ReorderApp({ shell }) {
  const loggedInBuyer = localStorage.getItem("reorder_buyer") || null;
  // 2026-10-07 accounts: "sees everything" comes from the user (admin / ecom / all-departments buyer);
  // otherwise visibility is per department via canSeeDepartment.
  const acctUser = getAccountsUser();
  // isAdmin = admin-level Reorder screens (admin / ecom / all-departments buyer) - unchanged. acctSeesAll = department visibility:
  // 2026-10-13 the server's dept_access.all, so an Ecom (or WH / CRM) person given departments sees only those.
  const isAdmin = !!acctUser && (acctUser.team === "admin" || acctUser.team === "ecom" || !!acctUser.all_departments);
  const [styleProfitOn] = useStyleProfitPref(); // 2026-10-26 per-person "Style profit search" preference (users.prefs.style_profit_search)
  const acctSeesAll = !!acctUser && (acctUser.dept_access && typeof acctUser.dept_access.all === "boolean" ? acctUser.dept_access.all : isAdmin);
  const isReadOnly = READONLY_BUYERS.includes(loggedInBuyer);
  const isEcom = loggedInBuyer === "Ecom";
  const isActualAdmin = loggedInBuyer === "Admin";
  const isShailendra = loggedInBuyer === "Shailendra";
  const isSubMember = localStorage.getItem("reorder_is_sub_member") === "true";
  const subDepts = useMemo(() => {
    if (!isSubMember) return [];
    try { return JSON.parse(localStorage.getItem("reorder_sub_departments") || "[]"); } catch { return []; }
  }, [isSubMember]);
  const subVendors = useMemo(() => {
    if (!isSubMember) return [];
    try { return JSON.parse(localStorage.getItem("reorder_sub_vendors") || "[]"); } catch { return []; }
  }, [isSubMember]);

  const [poStatus, setPoStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  // allowedBuyers: null = all access, array = buyer (owner) names this login may see
  const allowedBuyers = useMemo(() => {
    if (acctSeesAll) return null;
    const names = new Set();
    if (acctUser && acctUser.name) names.add(acctUser.name);
    sessions.forEach((s) => (s.departments || []).forEach((d) => { if (canSeeDept(d)) { const b = getBuyer(d); if (b) names.add(b); } }));
    return [...names];
  }, [acctSeesAll, acctUser, sessions]);
  const [activeId, setActiveId] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [overall, setOverall] = useState(null);
  const [consolidated, setConsolidated] = useState(null);
  const [expiredPo, setExpiredPo] = useState(null);
  const [minValidityDays, setMinValidityDays] = useState(14);
  const [vendorScore, setVendorScore] = useState(null);
  const [vendorResponses, setVendorResponses] = useState(null);
  // "boot" until the first session list arrives, so the upload card no longer flashes (and fires autoImportStatus) before Overall.
  const [view, setView] = useState("boot");
  const [loadErr, setLoadErr] = useState(null); // { view, msg } - a failed load shows an error with Retry instead of an empty page
  const [reloadTick, setReloadTick] = useState(0);
  const retryLoad = useCallback(() => setReloadTick((n) => n + 1), []);
  // 2026-10-07 single Admin hub, split 2026-10-09: "admin-settings" = Reorder settings (General rules only, AdminPortal
  // scope="reorder"); the old hub views (WhatsApp Reports, Log API Shared Data)
  // now open the app-level Settings view (header user menu) on the matching section via shell.onOpenSettings.
  // Must stay BELOW the `view` state: it was once placed above it and crashed the Reorder app (TDZ, 2026-10-07).
  const adminHubView = (isAdmin && !isEcom) && ["admin-settings", ...APP_SETTINGS_VIEWS].includes(view);
  const appSettingsHubView = adminHubView && view !== "admin-settings";
  const lastViewRef = useRef("overall");
  useEffect(() => { if (view !== "boot" && !(adminHubView && APP_SETTINGS_VIEWS.includes(view))) lastViewRef.current = view; }, [view, adminHubView]);
  // Old deep link to an app-wide section: hand it to the app-level Settings view and keep Reorder where it was.
  useEffect(() => {
    if (!appSettingsHubView || !shell.onOpenSettings) return;
    shell.onOpenSettings(view);
    setView(lastViewRef.current);
  }, [appSettingsHubView, view, shell]);
  const [buyerFilter, setBuyerFilter] = useState(() =>
    allowedBuyers ? allowedBuyers[0] : "All"
  );
  const [loading, setLoading] = useState(false);
  const [toast, showToast]            = useToast();
  const [deleteModal, setDeleteModal]   = useState(null); // session object
  const [toggleModal, setToggleModal]   = useState(null); // session object
  const [sessionRejections, setSessionRejections] = useState({});
  const [sendingBuyer, setSendingBuyer] = useState(null);
  const [sendingBuyerReminder, setSendingBuyerReminder] = useState(null);
  const [reminderCooldowns, setReminderCooldowns] = useState({});
  const [vendorList, setVendorList] = useState([]);
  const [excessPct, setExcessPct] = useState(10); // default 10%
  const [appSettings, setAppSettings] = useState({});
  const [loadImages, setLoadImages] = useState(() => {
    return localStorage.getItem("setting_load_images") === "true";
  });
  const [pendingCount, setPendingCount] = useState(0);
  const [cpApprovalsCount, setCpApprovalsCount] = useState(0);
  const [noBarcodeCount, setNoBarcodeCount] = useState(0);
  const [styleRates, setStyleRates] = useState({});
  // 2026-10-12 flow strip: PO-status filter to open the session mapping with (only when arriving from the strip).
  const [mapStatusInit, setMapStatusInit] = useState(null);
  useEffect(() => { if (view !== "detail") setMapStatusInit(null); }, [view]);
  // Flow strip click on a pending part -> the page where that work gets done (targets from FlowStrip.flowTarget).
  const handleFlowNavigate = useCallback((t) => {
    if (!t || !t.view) return;
    if (t.view === "detail") { if (!t.sessionId) return; setMapStatusInit(t.statusFilter || null); setActiveId(t.sessionId); setView("detail"); return; }
    if (t.view === "vendor-pdfs" && t.sessionId) setActiveId(t.sessionId);
    setView(t.view);
  }, []);
  // Same rules as the sidebar items: Vendor PDFs = isAdmin, CP Value Approvals = isAdmin.
  const flowCanOpen = useCallback((v) => (v === "vendor-pdfs" || v === "cp-approvals" ? isAdmin : true), [isAdmin]);

  // Load style rates (admin only, used in MappingTable)
  useEffect(() => {
    if (isAdmin) getStyleRates().then(setStyleRates).catch(() => {});
  }, [isAdmin]);

  // Poll no-barcode queue count for badge (admin only). Skipped while the browser tab is hidden.
  useEffect(() => {
    if (!isAdmin) return;
    const refresh = () => !document.hidden && getPendingBarcodes().then(d => setNoBarcodeCount(Array.isArray(d) ? d.length : 0)).catch(() => {});
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, [isAdmin]);

  const handleSetRate = useCallback(async (styleNumber, rate) => {
    try {
      await setStyleRate(styleNumber, rate);
      setStyleRates(prev => {
        const next = { ...prev };
        if (rate == null) delete next[styleNumber];
        else next[styleNumber] = { rate, status: "pending", updated_at: new Date().toISOString(), api_sent_at: null };
        return next;
      });
    } catch { /* ignore */ }
  }, []);

  const refreshPendingCount = useCallback(() => {
    if (!isAdmin || document.hidden) return;
    listPendingUploads().then(d => setPendingCount(Array.isArray(d) ? d.filter(x => x.status !== "processing").length : 0)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    refreshPendingCount();
    const t = setInterval(refreshPendingCount, 60000);
    return () => clearInterval(t);
  }, [refreshPendingCount]);

  const refreshCpApprovalsCount = useCallback(() => {
    if (!isAdmin || document.hidden) return;
    getCpApprovals("pending").then(d => setCpApprovalsCount(Array.isArray(d.items) ? d.items.length : 0)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    refreshCpApprovalsCount();
    const t = setInterval(refreshCpApprovalsCount, 60000);
    return () => clearInterval(t);
  }, [refreshCpApprovalsCount]);

  const toggleImages = useCallback(() => {
    setLoadImages((v) => {
      localStorage.setItem("setting_load_images", String(!v));
      return !v;
    });
  }, []);

  const handleLogout = useCallback(() => { accountsLogout(); }, []);

  // Buyers only see sessions that contain at least one item from their department(s)
  const visibleSessions = useMemo(() => {
    if (!allowedBuyers) return sessions; // admin sees everything
    let filtered = sessions.filter((s) =>
      (s.departments || []).some((dept) => canSeeDept(dept))
    );
    // Hide a session entirely once every buyer this login can see has archived
    // (no order given this cycle) all of their own styles in it — nothing left
    // for this login to act on there.
    filtered = filtered.filter((s) =>
      !allowedBuyers.every((b) => (s.fully_archived_buyers || []).includes(b))
    );
    // Shailendra sub-members: further filter by their assigned departments
    if (isSubMember && isShailendra && (subDepts.length > 0 || subVendors.length > 0)) {
      filtered = filtered.filter((s) =>
        (s.departments || []).some((dept) => subDepts.includes(dept))
        // Note: vendor-based filtering (subVendors) is applied inside MappingTable
        // We show the session if it has at least one matching dept, or if subDepts is empty (vendor-only filter)
        || (subDepts.length === 0 && subVendors.length > 0)
      );
    }
    return filtered;
  }, [sessions, allowedBuyers, isSubMember, isShailendra, subDepts, subVendors]);

  const fetchSessions = useCallback(async () => {
    try {
      const data = await listUploads();
      setSessions(data);
      return data;
    } catch {
      showToast("Could not reach backend. Is it running on port 8001?", "error");
      return [];
    }
  }, [showToast]);

  useEffect(() => {
    getPoStatus().then(setPoStatus).catch(() => setPoStatus({ available: false }));
    fetchSessions().then((data) => setView((v) => (v === "boot" ? (data.length > 0 ? "overall" : "upload") : v))).catch(() => setView((v) => (v === "boot" ? "upload" : v)));
    getAppSettings().then((s) => { setExcessPct(s.excess_variance_pct || 10); setAppSettings(s); }).catch(() => {});
  }, [fetchSessions]);

  const reloadVendorList = useCallback(() => {
    listVendors().then(setVendorList).catch(() => {});
  }, []);

  useEffect(() => {
    if (!activeId || view !== "detail") return;
    setLoading(true);
    setMapping(null);
    setSessionRejections({});
    setBuyerFilter(allowedBuyers ? allowedBuyers[0] : "All");
    setTableFilteredItems(null);
    setLoadErr(null);
    getMapping(activeId)
      .then(setMapping)
      .catch((err) => { const m = err.response?.data?.detail || "Failed to load mapping"; setLoadErr({ view: "detail", msg: m }); showToast(m, "error"); })
      .finally(() => setLoading(false));
    reloadVendorList();
  }, [activeId, view, showToast, reloadVendorList, reloadTick]);

  // Silent re-fetch of the active session's mapping — e.g. after "+ Add Style" inserts
  // a new row that needs the server's computed PO/GRN/excess fields. Does not touch
  // `loading` so it doesn't flash the whole table into a spinner state.
  const reloadMapping = useCallback(() => {
    if (!activeId) return;
    getMapping(activeId)
      .then(setMapping)
      .catch((err) => showToast(err.response?.data?.detail || "Failed to refresh mapping", "error"));
  }, [activeId, showToast]);

  useEffect(() => {
    if (view !== "overall") return;
    setLoading(true);
    setOverall(null);
    setLoadErr(null);
    getOverall()
      .then(setOverall)
      .catch((err) => { const m = err.response?.data?.detail || "Failed to load overall summary"; setLoadErr({ view: "overall", msg: m }); showToast(m, "error"); })
      .finally(() => setLoading(false));
  }, [view, showToast, reloadTick]);

  // Fetch reminder cooldown status whenever the overall view is shown (Admin/Ecom only)
  useEffect(() => {
    if (view !== "overall") return;
    if (!isAdmin && !isEcom) return;
    getReminderCooldowns()
      .then((data) => setReminderCooldowns(data.buyers || {}))
      .catch(() => {}); // non-critical — button will default to unlocked if fetch fails
  }, [view, isAdmin, isEcom]);

  useEffect(() => {
    if (view !== "consolidated") return;
    setLoading(true);
    setConsolidated(null);
    setLoadErr(null);
    // Phase 1: first 100 rows fast
    getConsolidatedPage(100, 0)
      .then((data) => {
        setConsolidated(data);
        setLoading(false);
        // Phase 2: silently load all in background if more exist
        if (data.has_more) {
          getConsolidatedPage(0, 0).then(setConsolidated).catch(() => {});
        }
      })
      .catch((err) => {
        const m = err.response?.data?.detail || "Failed to load consolidated view";
        setLoadErr({ view: "consolidated", msg: m });
        showToast(m, "error");
        setLoading(false);
      });
  }, [view, showToast, reloadTick]);

  useEffect(() => {
    if (view !== "expired") return;
    setLoading(true);
    setExpiredPo(null);
    setLoadErr(null);
    getExpiredPos(100, 0, minValidityDays)
      .then((data) => {
        setExpiredPo(data);
        setLoading(false);
        if (data.has_more) {
          getExpiredPos(0, 0, minValidityDays).then(setExpiredPo).catch(() => {});
        }
      })
      .catch((err) => {
        const m = err.response?.data?.detail || "Failed to load expired POs";
        setLoadErr({ view: "expired", msg: m });
        showToast(m, "error");
        setLoading(false);
      });
  }, [view, showToast, minValidityDays, reloadTick]);

  useEffect(() => {
    if (view !== "score") return;
    setLoading(true);
    setVendorScore(null);
    setLoadErr(null);
    getVendorScore()
      .then(setVendorScore)
      .catch((err) => { const m = err.response?.data?.detail || "Failed to load vendor scores"; setLoadErr({ view: "score", msg: m }); showToast(m, "error"); })
      .finally(() => setLoading(false));
  }, [view, showToast, reloadTick]);

  const reloadVendorResponses = useCallback(() => {
    setLoading(true);
    getVendorResponses()
      .then(setVendorResponses)
      .catch((err) => { const m = err.response?.data?.detail || "Failed to load responses"; setLoadErr({ view: "vendor-responses", msg: m }); showToast(m, "error"); })
      .finally(() => setLoading(false));
  }, [showToast]);

  useEffect(() => {
    if (view !== "vendor-responses") return;
    setVendorResponses(null);
    setLoadErr(null);
    reloadVendorResponses();
  }, [view, reloadVendorResponses, reloadTick]);

  const handleMarkItReady = useCallback(async (id) => {
    await markVendorResponseItReady(id, "");
    reloadVendorResponses();
  }, [reloadVendorResponses]);

  // Filtered items and recomputed stats based on buyer tab
  const filteredItems = useMemo(() => {
    if (!mapping) return [];
    if (buyerFilter === "All") return !acctSeesAll ? mapping.items.filter((i) => canSeeDept(i.department)) : mapping.items; // 2026-10-07 accounts
    return mapping.items.filter((i) => getBuyer(i.department) === buyerFilter && (acctSeesAll || canSeeDept(i.department)));
  }, [mapping, buyerFilter, acctSeesAll]);

  const [tableFilteredItems, setTableFilteredItems] = useState(null);

  const activeStats = useMemo(() => {
    if (!mapping) return null;
    const baseItems = tableFilteredItems || (buyerFilter === "All" ? mapping.items : filteredItems);
    const base = computeStats(baseItems); // always recompute — keeps non_ecom_po as "created"
    const rejectedItems = baseItems.filter(
      (i) => sessionRejections[i.style_number] && !i.po_created && !i.non_ecom_po
    );
    const buyerRejectedCount = rejectedItems.length;
    const buyer_rejected_qty = rejectedItems.reduce((s, i) => s + (i.total_requested_qty || 0), 0);
    return {
      ...base,
      po_pending: Math.max(0, base.po_pending - buyerRejectedCount),
      buyer_rejected: buyerRejectedCount,
      buyer_rejected_qty,
    };
  }, [mapping, buyerFilter, filteredItems, tableFilteredItems, sessionRejections]);

  // Per-buyer: "all" = all eligible vendors emailed, "some" = partial, "none" = none
  const buyerSentStatus = useMemo(() => {
    if (!mapping || vendorList.length === 0) return {};
    const status = {};
    for (const b of BUYERS.filter((b) => b !== "All")) {
      const suppliers = [...new Set(
        mapping.items.filter((i) => getBuyer(i.department) === b).map((i) => i.supplier).filter(Boolean)
      )];
      const eligible = vendorList.filter(
        (v) => !v.auto_detected && v.enabled && v.email &&
          suppliers.some((s) => s.toLowerCase() === v.name.toLowerCase())
      );
      if (eligible.length === 0) { status[b] = "none"; continue; }
      const emailedCount = eligible.filter((v) => v.last_emailed).length;
      status[b] = emailedCount === eligible.length ? "all" : emailedCount > 0 ? "some" : "none";
    }
    return status;
  }, [mapping, vendorList]);

  // Items per buyer for the tab badges - counted once per mapping instead of once per tab on every render.
  const buyerCounts = useMemo(() => {
    const c = {};
    if (mapping) mapping.items.forEach((i) => { const b = getBuyer(i.department); c[b] = (c[b] || 0) + 1; });
    return c;
  }, [mapping]);

  const handleSendBuyerReminder = useCallback(async (buyer) => {
    setSendingBuyerReminder(buyer);
    try {
      const result = await sendBuyerReminder(buyer);
      if (result.skipped) {
        showToast(`No pending POs found for ${buyer}`, "info");
      } else {
        showToast(`✅ Reminder sent to ${buyer} — ${result.total_pending} pending style${result.total_pending !== 1 ? "s" : ""} across ${result.sessions_count} session${result.sessions_count !== 1 ? "s" : ""}`, "info");
        // Refresh cooldown state so the button immediately shows as locked
        getReminderCooldowns()
          .then((data) => setReminderCooldowns(data.buyers || {}))
          .catch(() => {});
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || "Failed to send reminder";
      showToast(msg, "error");
    } finally {
      setSendingBuyerReminder(null);
    }
  }, [showToast]);

  const handleUploaded = async (sessionId) => {
    try {
      await fetchSessions();
    } catch { /* fetchSessions has its own catch, this is a safeguard */ }
    setActiveId(sessionId);
    setView("detail");
  };

  const handleDelete = (id) => {
    const session = sessions.find((s) => s._id === id);
    setDeleteModal(session || { _id: id });
  };

  const confirmDelete = async (pin) => {
    const id = deleteModal._id;
    await deleteSession(id, pin);
    setDeleteModal(null);
    const data = await fetchSessions();
    if (id === activeId) {
      setActiveId(null);
      setView(data.length > 0 ? "overall" : "upload");
      setMapping(null);
    }
    showToast("Session deleted", "info");
  };

  const handleToggleConfirmation = (id) => {
    const session = sessions.find((s) => s._id === id);
    setToggleModal(session || { _id: id });
  };

  const confirmToggle = async () => {
    const id = toggleModal._id;
    setToggleModal(null);
    try {
      const { confirmation_enabled } = await toggleConfirmation(id);
      await fetchSessions();
      showToast(confirmation_enabled ? "Session now visible to vendors" : "Session hidden from vendors", "info");
    } catch {
      showToast("Failed to update", "error");
    }
  };

  const handleArchive = async (id) => {
    if (!window.confirm("Archive this session? It will no longer appear in your view.")) return;
    try {
      await archiveSession(id);
      const data = await fetchSessions();
      if (id === activeId) {
        setActiveId(null);
        setView(data.length > 0 ? "overall" : "upload");
        setMapping(null);
      }
      showToast("Session archived", "info");
    } catch {
      showToast("Archive failed", "error");
    }
  };

  const handleSendBuyerEmails = async (buyer) => {
    if (!mapping) return;
    const suppliers = [...new Set(
      mapping.items
        .filter((i) => getBuyer(i.department) === buyer)
        .map((i) => i.supplier)
        .filter(Boolean)
    )];
    if (suppliers.length === 0) {
      showToast(`No suppliers found for ${buyer}`, "error");
      return;
    }
    const alreadySent = buyerSentStatus[buyer] === "all";
    const msg = alreadySent
      ? `Resend to ${buyer} vendors with pending confirmation?\n(Vendors who submitted all styles will be skipped)\n\nVendors: ${suppliers.join(", ")}`
      : `Send email links to all ${buyer} vendors?\n\n${suppliers.join(", ")}`;
    if (!window.confirm(msg)) return;
    setSendingBuyer(buyer);
    try {
      const { sent, skipped, errors } = await sendBulkVendorEmails(suppliers, alreadySent);
      const errCount = Object.keys(errors).length;
      showToast(
        `${buyer}: ${sent.length} sent` +
        (skipped.length ? `, ${skipped.length} skipped` : "") +
        (errCount ? `, ${errCount} failed` : ""),
        errCount ? "error" : "info"
      );
      reloadVendorList();
    } catch (err) {
      showToast(err.response?.data?.detail || "Email sending failed", "error");
    } finally {
      setSendingBuyer(null);
    }
  };

  return (
    <div className="app-layout">
      <Header poStatus={poStatus} onRefreshed={() => getPoStatus().then(setPoStatus).catch(() => {})} showToast={showToast} readOnly={isReadOnly}
        module="reorder" modules={shell.modules} onSwitchModule={shell.onSwitch} onHome={shell.onHome} onOpenSettings={shell.onOpenSettings ? () => shell.onOpenSettings("") : shell.onOpenTeam || null} settingsLabel={shell.onOpenSettings ? undefined : "Team"} />
      <div className="app-body">
        <>
        <Sidebar
          showStyleProfit={showStyleProfitFor(acctUser, styleProfitOn)}
          sessions={visibleSessions}
          activeId={view === "detail" ? activeId : null}
          onSelect={(id) => { setMapStatusInit(null); setActiveId(id); setView("detail"); }}
          onNew={() => { setActiveId(null); setView("upload"); }}
          newActive={view === "upload"}
          canUpload={true}
          onDelete={isActualAdmin ? handleDelete : null}
          onToggleConfirmation={!isReadOnly ? handleToggleConfirmation : null}
          onOverall={() => setView("overall")}
          overallActive={view === "overall"}
          onConsolidated={() => setView("consolidated")}
          consolidatedActive={view === "consolidated"}
          expiredActive={view === "expired"}
          onExpired={() => setView("expired")}
          scoreActive={view === "score"}
          onScore={() => setView("score")}
          vendorMasterActive={view === "vendor-master"}
          onVendorMaster={() => setView("vendor-master")}
          vendorResponsesActive={view === "vendor-responses"}
          onVendorResponses={() => setView("vendor-responses")}
          loadImages={loadImages}
          onToggleImages={toggleImages}
          isAdmin={isAdmin}
          isEcom={isEcom}
          onSettings={() => setView("admin-settings")}
          settingsActive={adminHubView}
          onBlacklist={() => setView("blacklist")}
          blacklistActive={view === "blacklist"}
          onNewColours={() => setView("new-colours")}
          newColoursActive={view === "new-colours"}
          onPendingApprovals={() => setView("pending-approvals")}
          pendingApprovalsActive={view === "pending-approvals"}
          pendingCount={pendingCount}
          onCpApprovals={() => setView("cp-approvals")}
          cpApprovalsActive={view === "cp-approvals"}
          cpApprovalsCount={cpApprovalsCount}
          onApiExportLog={() => setView("api-export-log")}
          apiExportLogActive={view === "api-export-log"}
          onWaReports={() => setView("wa-reports")}
          waReportsActive={view === "wa-reports"}
                onBarcodes={() => setView("barcodes")}
                barcodesActive={view === "barcodes"}
          onNoBarcodeQueue={() => setView("no-barcode-queue")}
          noBarcodeQueueActive={view === "no-barcode-queue"}
          noBarcodeCount={noBarcodeCount}
          onVendorPdfs={() => setView("vendor-pdfs")}
          vendorPdfsActive={view === "vendor-pdfs"}
          onLogout={handleLogout}
          readOnly={isReadOnly}
        />
        <div className="main-content">
          <Suspense fallback={pageFallback}>

          {view === "boot" && pageFallback}

          {view === "upload" && (
            <UploadSection onUploaded={handleUploaded} showToast={showToast} />
          )}

          {loading && (
            <div className="state-center" role="status">
              <div style={{ width: "min(320px, 80%)" }}><LoadingBar /></div>
              <p>{view === "overall" ? "Loading overall summary…" : view === "consolidated" ? "Loading all orders…" : view === "expired" ? "Loading expired POs…" : view === "score" ? "Loading vendor scores…" : view === "vendor-responses" ? "Loading responses…" : "Loading PO mapping…"}</p>
            </div>
          )}

          {view === "overall" && !loading && !overall && (
            loadErr?.view === "overall" ? <ErrorState message={loadErr.msg} onRetry={retryLoad} /> : <EmptyState title="No sessions yet" />
          )}
          {view === "overall" && !loading && overall && (
            <OverallDashboard
              showStyleProfit={showStyleProfitFor(acctUser, styleProfitOn)}
              data={overall}
              allowedBuyers={allowedBuyers}
              onSessionClick={(id) => { setActiveId(id); setView("detail"); }}
              onSendReminder={(isAdmin || isEcom) ? handleSendBuyerReminder : null}
              sendingReminder={sendingBuyerReminder}
              reminderCooldowns={reminderCooldowns}
              onFlowNavigate={handleFlowNavigate}
              flowCanOpen={flowCanOpen}
            />
          )}

          {view === "consolidated" && !loading && !consolidated && (
            loadErr?.view === "consolidated" ? <ErrorState message={loadErr.msg} onRetry={retryLoad} /> : <EmptyState title="No orders yet" />
          )}
          {view === "consolidated" && !loading && consolidated && (
            <ConsolidatedView data={consolidated} loadImages={loadImages} allowedBuyers={allowedBuyers} showToast={showToast} loggedInBuyer={loggedInBuyer} isAdmin={isAdmin} />
          )}

          {view === "expired" && !loading && !expiredPo && (
            loadErr?.view === "expired" ? <ErrorState message={loadErr.msg} onRetry={retryLoad} /> : <EmptyState title="No expired POs yet" />
          )}
          {view === "expired" && !loading && expiredPo && (
            <ExpiredPoPage data={expiredPo} minValidityDays={minValidityDays} onMinValidityChange={setMinValidityDays} allowedBuyers={allowedBuyers} />
          )}

          {view === "score" && !loading && !vendorScore && (
            loadErr?.view === "score" ? <ErrorState message={loadErr.msg} onRetry={retryLoad} /> : <EmptyState title="No vendor scores yet" />
          )}
          {view === "score" && !loading && vendorScore && (
            <VendorScorePage data={vendorScore} allowedBuyers={allowedBuyers} />
          )}

          {view === "vendor-pdfs" && (
            <VendorPdfsPage activeSessionId={activeId} sessions={visibleSessions} showToast={showToast} />
          )}

          {view === "vendor-master" && !loading && (
            <VendorMasterPage showToast={showToast} allowedBuyers={allowedBuyers} readOnly={isReadOnly} />
          )}

          {view === "vendor-responses" && !loading && !vendorResponses && (
            loadErr?.view === "vendor-responses" ? <ErrorState message={loadErr.msg} onRetry={retryLoad} /> : <EmptyState title="No responses yet" />
          )}
          {view === "vendor-responses" && !loading && vendorResponses && (
            <VendorResponsesPage data={vendorResponses} onReload={reloadVendorResponses} showToast={showToast} allowedBuyers={allowedBuyers} readOnly={isReadOnly} styleRates={styleRates} onSetRate={handleSetRate} appSettings={appSettings} onMarkItReady={handleMarkItReady} />
          )}

          {view === "admin-settings" && adminHubView && (
            <AdminPortal inline scope="reorder" showToast={showToast}
              onOpenAppSettings={shell.onOpenSettings ? () => shell.onOpenSettings("") : null} />
          )}
          {/* No app Settings access (non-admin "sees all" accounts): the old hub view still renders here, as before. */}
          {appSettingsHubView && !shell.onOpenSettings && (
            <AdminPortal inline showToast={showToast} initialTab={view} />
          )}

          {view === "blacklist" && (
            <BlacklistPage loggedInBuyer={loggedInBuyer} isAdmin={isAdmin} />
          )}

          {view === "new-colours" && (
            <BlockbusterPage showToast={showToast} buyer={loggedInBuyer} isAdmin={isAdmin} />
          )}

          {view === "pending-approvals" && (
            <PendingApprovalsPage
              showToast={showToast}
              onApproved={() => { fetchSessions(); refreshPendingCount(); }}
              readOnly={!isActualAdmin}
            />
          )}

          {view === "cp-approvals" && (
            <CpApprovalsPage
              showToast={showToast}
              onDecided={refreshCpApprovalsCount}
              showProfitability={!!appSettings.cp_approvals_show_profitability}
              loadImages={loadImages}
            />
          )}

          {view === "barcodes" && (
            <BarcodesPage showToast={showToast} />
          )}

          {view === "no-barcode-queue" && (
            <NoBarcodeQueuePage showToast={showToast} />
          )}

          {view === "detail" && !loading && !mapping && (
            loadErr?.view === "detail" ? <ErrorState message={loadErr.msg} onRetry={retryLoad} /> : <EmptyState title="Select an upload from the sidebar" />
          )}
          {view === "detail" && !loading && mapping && (
            <>
              {/* Buyer filter tabs */}
              <div className="buyer-tabs">
                {(isAdmin ? BUYERS : allowedBuyers).map((b) => {
                  const color = b !== "All" ? BUYER_COLORS[b] : null;
                  const isActive = buyerFilter === b;
                  return (
                    <button
                      key={b}
                      className={`buyer-tab ${isActive ? "active" : ""}`}
                      style={isActive && color ? { background: color.bg, color: color.text, borderColor: color.text } : {}}
                      onClick={() => { setBuyerFilter(b); setTableFilteredItems(null); }}
                    >
                      {b === "All" ? "All Buyers" : b}
                      {b !== "All" && (
                        <span className="buyer-tab-count">
                          {buyerCounts[b] || 0}
                        </span>
                      )}
                      {b !== "All" && !isReadOnly && (
                        <span
                          className={`buyer-tab-send${sendingBuyer === b ? " sending" : ""}${buyerSentStatus[b] === "all" ? " sent" : ""}`}
                          title={buyerSentStatus[b] === "all" ? `Resend to ${b} vendors with pending confirmation` : `Send email to all ${b} vendors`}
                          onClick={(e) => { e.stopPropagation(); handleSendBuyerEmails(b); }}
                        >
                          {sendingBuyer === b ? "⏳" : buyerSentStatus[b] === "all" ? "✅" : "📧"}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <StatsCards stats={activeStats} />
              <OtbNotRaisedBanner buyer={buyerFilter} showToast={showToast} onOpenOtb={shell.modules && shell.modules.includes("ecom_otb") ? () => shell.onSwitch("ecom_otb") : null} />
              <MappingTable
                items={filteredItems}
                session={mapping.session}
                poFile={mapping.po_file}
                onFilteredChange={setTableFilteredItems}
                onRejectionsChange={setSessionRejections}
                loadImages={loadImages}
                sessionId={activeId}
                loggedInBuyer={loggedInBuyer}
                isAdmin={isAdmin}
                isReadOnly={isReadOnly}
                showToast={showToast}
                excessPct={excessPct}
                subDepts={subDepts}
                subVendors={subVendors}
                styleRates={styleRates}
                onSetRate={handleSetRate}
                onReloadMapping={reloadMapping}
                initialStatusFilter={mapStatusInit}
                reviewedBuyers={mapping.reviewed_buyers}
              />
            </>
          )}

          </Suspense>
        </div>
        </>
      </div>

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

      {deleteModal && (
        <DeleteSessionModal
          session={deleteModal}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteModal(null)}
        />
      )}

      {toggleModal && (
        <ToggleConfirmModal
          session={toggleModal}
          onConfirm={confirmToggle}
          onCancel={() => setToggleModal(null)}
        />
      )}
    </div>
  );
}

// ── Ops-hub shell: resolves allowed modules, launcher, and non-Reorder modules ──
const OPS_MODULE_KEY = "ops_module";
const lsGet = (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, v); } catch (e) { console.error("localStorage unavailable", e); } };

export default function App() {
  const login = lsGet("reorder_buyer") || "";
  const [modules, setModules] = useState(null);
  const [modulesError, setModulesError] = useState(false); // 2026-10-07 accounts: tools failed to load -> retry state (no fallback grants)
  const [modulesTry, setModulesTry] = useState(0);
  const [module, setModule] = useState(null); // null = launcher
  const [toast, showToast] = useToast();

  useEffect(() => {
    let alive = true;
    setModulesError(false);
    getMyModules()
      .then((d) => d.modules || [])
      .catch((err) => { console.error("[modules] /api/me/modules failed (no fallback grants):", err); return null; })
      .then((mods) => {
        if (!alive) return;
        if (mods === null) { setModulesError(true); return; }
        { // 2026-10-07 accounts: tools = the user's own tool list (all for admins), never wider than the server answer
          const u = getAccountsUser();
          // The public user carries its team's tools as `tools` (array). `team_tools` is only a top-level field of
          // /api/account/me, not on the user - reading u.team_tools alone left every non-admin with zero tools (caught 2026-10-08).
          // 2026-10-13: effective tools (team preset + Customise access overrides) via utils/access.
          const ownList = u && (u.tool_rights || Array.isArray(u.tools)) ? toolList(u) : Array.isArray(u?.team_tools) ? u.team_tools : [];
          const own = u ? (u.team === "admin" || u.is_admin ? Object.keys(MODULES) : ownList) : mods;
          mods = mods.filter((m) => own.includes(m));
          mods = mods.filter((m) => m !== "ars"); // 2026-10-30 ARS Management lives in Soch Tools (tools.sochone.com/ars): hidden here for everyone
        }
        setModules(mods);
        const saved = lsGet(OPS_MODULE_KEY);
        const legacy = lsGet("reorder_mode"); // pre-revamp key: "reorder" | "ars" | "profit"
        let start = null;
        if (mods.length === 1) start = mods[0];
        else if (saved && mods.includes(saved)) start = saved;
        else if (legacy && mods.includes(legacy)) start = legacy; // "profit" no longer exists -> launcher
        try { localStorage.removeItem("reorder_mode"); } catch (e) { /* ignore */ }
        setModule(start);
      });
    return () => { alive = false; };
  }, [login, modulesTry]);

  const handleLogout = useCallback(() => { accountsLogout(); }, []);

  // App-level Settings view: null = closed, otherwise the section to open ("" = first). The current tool stays
  // mounted (hidden) underneath, so closing Settings returns the user to exactly where they were.
  const [settingsTab, setSettingsTab] = useState(null);
  const settingsAllowed = canOpenAppSettings();
  const openSettings = useCallback((tab) => setSettingsTab(typeof tab === "string" ? tab : ""), []);
  const closeSettings = useCallback(() => setSettingsTab(null), []);

  const openModule = useCallback((id) => { lsSet(OPS_MODULE_KEY, id); setSettingsTab(null); setModule(id); }, []);
  const goHome = useCallback(() => {
    setSettingsTab(null);
    if (modules && modules.length === 1) setModule(modules[0]); else setModule(null);
  }, [modules]);

  const teamLead = !settingsAllowed && isTeamLead();
  const settingsLabel = settingsAllowed ? "Settings" : "Team";
  const settingsOpen = settingsTab !== null && (settingsAllowed || teamLead);
  useEffect(() => {
    document.title = settingsOpen ? `${settingsLabel} · SochOne` : module && MODULES[module] ? `${MODULES[module].title} · SochOne` : "SochOne Tools";
  }, [module, settingsOpen, settingsLabel]);

  if (modulesError) {
    return (
      <div className="state-center" style={{ height: "100vh" }}>
        <div style={{ width: "min(420px, 100%)" }}>
          <ErrorState message="Couldn't load your tools." onRetry={() => setModulesTry((n) => n + 1)} />
        </div>
      </div>
    );
  }

  if (!modules) {
    return <div className="state-center" style={{ height: "100vh" }}><div className="spinner" /><p>Loading your tools…</p></div>;
  }

  const shell = { modules, onSwitch: openModule, onHome: goHome, onOpenSettings: settingsAllowed ? openSettings : null, onOpenTeam: teamLead ? () => openSettings("") : null };
  const headerSettings = settingsAllowed || teamLead ? () => openSettings("") : null;
  const backLabel = module && MODULES[module] ? `Back to ${MODULES[module].title}` : "Back to all tools";
  const acct = getAccountsUser();

  const settingsView = settingsOpen ? (
    <div className="app-layout">
      <Header showToast={showToast} module={null} title={settingsLabel} modules={modules} onSwitchModule={openModule} onHome={goHome} onLogout={handleLogout} onOpenSettings={headerSettings} settingsLabel={settingsLabel} />
      <div className="app-body">
        <div className="main-content" style={{ width: "100%" }}>
          <Suspense fallback={pageFallback}>
            {settingsAllowed ? (
              <AdminPortal inline scope="app" showToast={showToast} initialTab={settingsTab} onClose={closeSettings}
                closeLabel={backLabel} />
            ) : (
              <>
                <PageHeader title="Team" actions={<Button variant="secondary" size="sm" onClick={closeSettings}>{"← "}{backLabel}</Button>} />
                <UsersAccessPage showToast={showToast} currentUserEmail={(acct && acct.email) || ""} />
              </>
            )}
          </Suspense>
        </div>
      </div>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </div>
  ) : null;
  // The tool keeps its state while Settings is open: it stays mounted, only hidden.
  const withSettings = (tool) => (
    <>
      <div style={{ display: settingsOpen ? "none" : "contents" }}>{tool}</div>
      {settingsView}
    </>
  );

  if (module === "reorder" && modules.includes("reorder")) return withSettings(<ReorderApp shell={shell} />);

  const displayName = lsGet("reorder_display_name") || login;
  const header = (
    <Header showToast={showToast} module={module} modules={modules} onSwitchModule={openModule} onHome={goHome} onLogout={handleLogout} onOpenSettings={headerSettings} settingsLabel={settingsLabel} />
  );
  let body;
  if (module === null) body = <AppLauncher modules={modules} onOpen={openModule} displayName={displayName} showToast={showToast} />;
  else if (module === "ars" && modules.includes("ars")) body = <ArsManagementPage showToast={showToast} />;
  else if (module === "wh_refill" && modules.includes("wh_refill")) body = <RegionalWhApp showToast={showToast} />;
  else if (module === "stock_reco" && modules.includes("stock_reco")) body = <StockRecoApp showToast={showToast} />;
  else if (module === "ecom_otb" && modules.includes("ecom_otb")) body = <EcomOtbApp showToast={showToast} />; // 2026-10-24
  else if (module === "channel_profit" && modules.includes("channel_profit")) body = <ChannelProfitApp showToast={showToast} />; // 2026-10-27
  else if (module === "discount_verify" && modules.includes("discount_verify")) body = <DiscountVerifyApp showToast={showToast} />;
  else body = <EmptyState title={`${MODULES[module]?.title || "This tool"} is not available for your login.`} />;

  return withSettings(
    <div className="app-layout">
      {header}
      <div className="app-body">
        <div className="main-content" style={{ width: "100%" }}><Suspense fallback={pageFallback}>{body}</Suspense></div>
      </div>
      {!settingsOpen && toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
